// v4.ts — Sprint 19BH Day 4 Step 4.3 · request/response Zod schemas for the
// 10 v4 endpoints introduced in this sprint. Frontend imports the inferred
// types directly so a schema change here surfaces as a compile error there.
//
// File organisation — co-located on purpose:
//   v4 contract is a single product surface; splitting into 10 files would
//   force the reader to chase imports just to understand one user flow
//   (e.g. regen Phase 1 + Phase 2 + history + rollback are 4 endpoints that
//   only make sense together). The handlers live in one-file-per-endpoint
//   under packages/ingest/src/api/endpoints/ — that's where the
//   "1 file = 1 endpoint" maintainability rule (plan §12.5) bites.
import { z } from 'zod';
import { CurriculumOutlineSchema } from '../schemas/curriculum-outline.js';
import { DisciplineSchema } from '../schemas/discipline.js';
import { ContentLanguageSchema } from '../schemas/content-language.js';
import { RegenActionSchema } from '../schemas/regen-action.js';
import { RegenHistoryEntrySchema } from '../schemas/regen-history.js';
import { MockPaperSpecV2Schema } from '../schemas/mock-paper-spec-v2.js';
import { GapReportSchema } from '../schemas/audit-gap-report.js';
import { IngestPackageSchema } from '../schemas/ingest-package.js';
import { LintReportSchema } from '../schemas/lint-report.js';
import { EvaluateItemKindSchema, EvaluateResultSchema } from '../schemas/evaluate-result.js';
import { DiagnoseResultSchema } from '../schemas/diagnose-result.js';
// ─────────────────────────────────────────────────────────────────────────────
//   Common building blocks
// ─────────────────────────────────────────────────────────────────────────────
/** Slug-style id used for course folders + IDB keys; mirrors CourseDraft.id. */
const CourseId = z.string().regex(/^[a-z][a-z0-9_-]*$/).max(64);
const AnonId = z.string().min(1).max(128);
/** All v4 async endpoints return this shape — frontend reuses the same poller. */
export const AsyncJobResponseSchema = z.object({
    runId: z.string(),
    instanceId: z.string().optional(),
    statusUrl: z.string(),
});
/** Lock-denial responses — used by every endpoint that calls acquireCourseLock. */
export const LockDeniedResponseSchema = z.object({
    error: z.literal('lock_denied'),
    reason: z.enum([
        'course_busy', 'anon_too_many_inflight', 'anon_daily_cost_cap', 'mock_rate_limit',
    ]),
    detail: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/extract-outline
//   Single LLM call to produce a CurriculumOutline draft. User edits +
//   confirms before /ingest/start.
// ─────────────────────────────────────────────────────────────────────────────
export const ExtractOutlineRequestSchema = z.object({
    courseId: CourseId,
    courseTitle: z.string().min(1).max(120),
    /** Raw source (pdf base64) or pre-condensed text. One required. */
    sourceBase64: z.string().min(1).optional(),
    sourceText: z.string().min(1).optional(),
    hint: z.string().max(2000).optional(),
    // 19BO+: subject + language are threaded into the extract LLM call so the
    // outline is generated with the right discipline tone + content language
    // from the first call. Previously dropped at all 3 layers (schema/endpoint/
    // FE caller) — see DEBT-19BO+-LINE1-FAKE-QUICK-MODE.
    discipline: DisciplineSchema.optional(),
    customDiscipline: z.string().min(1).max(80).optional(),
    contentLanguage: ContentLanguageSchema.optional(),
}).refine((v) => v.sourceBase64 !== undefined || v.sourceText !== undefined, { message: 'either sourceBase64 or sourceText is required' });
export const ExtractOutlineResponseSchema = z.object({
    outline: CurriculumOutlineSchema,
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/lock-outline
//   Synchronous · freezes user-confirmed outline. Returns the outline back
//   so the frontend can stamp `source: 'llm_generated_frozen'` reliably.
// ─────────────────────────────────────────────────────────────────────────────
export const LockOutlineRequestSchema = z.object({
    courseId: CourseId,
    outline: CurriculumOutlineSchema,
});
export const LockOutlineResponseSchema = z.object({
    ok: z.literal(true),
    outline: CurriculumOutlineSchema,
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/audit
//   Runs the no-LLM audit framework (packages/ingest/src/audit) against the
//   caller-supplied IngestPackage and outline. Returns the GapReport.
//   Synchronous — audit is pure TS, ~5s worst case.
// ─────────────────────────────────────────────────────────────────────────────
export const AuditRequestSchema = z.object({
    courseId: CourseId,
    /** Caller supplies the package directly · avoids needing to hit D1 first. */
    pkg: z.unknown(),
    outline: CurriculumOutlineSchema,
});
export const AuditResponseSchema = z.object({
    gapReport: GapReportSchema,
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/regenerate · Phase 1
//   Accepts a RegenAction discriminated union. Returns asyncjob for polling.
//   The eventual /status row payload has shape:
//     { regenId, proposedItems, beforeSnapshot, diffSummary }
//   distinct from the full-ingest finalize response.
// ─────────────────────────────────────────────────────────────────────────────
export const RegenerateRequestSchema = z.object({
    courseId: CourseId,
    anonId: AnonId,
    action: RegenActionSchema,
});
export const RegenerateResponseSchema = AsyncJobResponseSchema.extend({
    /** ULID-style regen id; survives across Phase 1 result + Phase 2 confirm. */
    regenId: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/regenerate-confirm · Phase 2
//   Apply or reject Phase-1 proposed items. Edited items pass through verbatim.
// ─────────────────────────────────────────────────────────────────────────────
export const RegenerateConfirmRequestSchema = z.object({
    regenId: z.string().min(1).max(64),
    decision: z.enum(['apply', 'reject']),
    /** Allows user to edit each proposed item before applying. Length must
     *  match proposedItems[] in the regen_history row. */
    editedItems: z.array(z.unknown()).optional(),
});
export const RegenerateConfirmResponseSchema = z.object({
    ok: z.literal(true),
    status: z.enum(['applied', 'rejected']),
    /** Asynchronous confirm: value populated only after REGEN_APPLY_WORKFLOW
     *  completes. Omitted from initial response because confirm is
     *  fire-and-forget — callers should poll /ingest/status for final count. */
    appliedCount: z.number().int().nonnegative().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
//   GET /ingest/regen-history?courseId=...
// ─────────────────────────────────────────────────────────────────────────────
export const RegenHistoryQuerySchema = z.object({
    courseId: CourseId,
    /** Default 50 most-recent entries. */
    limit: z.number().int().min(1).max(500).optional(),
});
export const RegenHistoryResponseSchema = z.object({
    entries: z.array(RegenHistoryEntrySchema),
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/regen-rollback
// ─────────────────────────────────────────────────────────────────────────────
export const RegenRollbackRequestSchema = z.object({
    courseId: CourseId,
    anonId: AnonId,
    regenId: z.string().min(1).max(64),
});
export const RegenRollbackResponseSchema = AsyncJobResponseSchema;
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/generate-mock (usage-time)
// ─────────────────────────────────────────────────────────────────────────────
export const GenerateMockRequestSchema = z.object({
    courseId: CourseId,
    anonId: AnonId,
    spec: MockPaperSpecV2Schema,
});
export const GenerateMockResponseSchema = AsyncJobResponseSchema;
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/cancel
//   Cancels an in-flight workflow. Surfaces a "tokens consumed are not
//   refundable" note — frontend MUST show this to the user before acting.
// ─────────────────────────────────────────────────────────────────────────────
export const CancelRequestSchema = z.object({
    runId: z.string().min(1).max(128),
});
export const CancelResponseSchema = z.object({
    ok: z.boolean(),
    /** Tokens already consumed before cancellation — informational. */
    tokensInConsumed: z.number().int().nonnegative(),
    tokensOutConsumed: z.number().int().nonnegative(),
    costRmbConsumed: z.number().nonnegative(),
    /** Verbatim warning text to render in the UI confirmation dialog. */
    warning: z.literal('Tokens already consumed are non-refundable.'),
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/start
//   Returns the runId and workflow instance id for polling via /ingest/status.
// ─────────────────────────────────────────────────────────────────────────────
export const IngestStartResponseSchema = z.object({
    runId: z.string(),
    instanceId: z.string(),
    statusUrl: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
//   StatusResult — discriminated union for D1 result_json (Session 19 Phase 5b)
//
//   result_json stores one of three shapes depending on what produced it:
//     1. v4 regen workflow → { v4Regen: true, regenId, ... }
//     2. legacy regen sync → { partialRegen: true, stage, mode, items }
//     3. full ingest       → IngestPackage
//
//   Uses z.union() with ordered fallback — no D1 migration needed:
//     - v4 regen has `v4Regen: true` literal (distinctive)
//     - legacy regen has `partialRegen: true` literal (distinctive)
//     - IngestPackage requires courseId/courseTitle/cases/flashcards/mcqs
//       (neither regen shape provides these → no misidentification)
// ─────────────────────────────────────────────────────────────────────────────
export const StatusResultSchema = z.union([
    // v4 regen — tried first (has v4Regen: true)
    z.object({
        v4Regen: z.literal(true),
        regenId: z.string(),
        action: RegenActionSchema,
        beforeSnapshot: z.array(z.unknown()),
        proposedItems: z.array(z.unknown()),
        diffSummary: z.object({
            addedCount: z.number(),
            removedCount: z.number(),
            replacedCount: z.number(),
        }),
        stageError: z.string().optional(),
    }),
    // Legacy regen — tried second (has partialRegen: true)
    z.object({
        partialRegen: z.literal(true),
        stage: z.string(),
        chapterId: z.string().optional(),
        mode: z.enum(['replace', 'add']),
        items: z.array(z.unknown()),
        stageError: z.string().optional(),
    }),
    // Full ingest — fallback (requires courseId + cases + flashcards + mcqs)
    IngestPackageSchema,
]);
// ─────────────────────────────────────────────────────────────────────────────
//   GET /ingest/status?runId=...
//   Polls the status of an ingest/regen workflow run.
//   `package` is the validated StatusResult discriminated union (Phase 5b).
// ─────────────────────────────────────────────────────────────────────────────
export const IngestStatusResponseSchema = z.object({
    runId: z.string(),
    status: z.enum(['started', 'completed', 'failed', 'aborted']),
    currentPhase: z.string().nullable(),
    stagesDone: z.number(),
    stagesFailed: z.number(),
    lastStage: z.string().nullable(),
    lastCheckpointAt: z.number().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    costRmb: z.number(),
    durationMs: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    workflowInstanceId: z.string().nullable().optional(),
    startedAt: z.number().nullable().optional(),
    completedAt: z.number().nullable().optional(),
    // Session 39: replaced z.unknown() with concrete schemas.
    // workflowStatus: CF Workflow instance.status() output | error object | null.
    workflowStatus: z.union([
        z.record(z.string(), z.unknown()), // CF instance.status() — shape varies by SDK version
        z.object({ error: z.string() }),
    ]).nullable(),
    package: StatusResultSchema.nullable(),
    // lintReport: LintReportSchema exists — use it instead of z.unknown().
    lintReport: LintReportSchema.nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/evaluate · 19BO+ Line 2 B3 · AI 评估主观题答案
//
//   同步调用 (非 async job · 与 /repair/item 同构 · 单次 LLM 调用返回 verdict)。
//   前端传被评 item 的 JSON (Essay/FreeResponse 整体) + userAnswer + 可选 history。
//   后端用对应 schema 解析 item, 提取 prompt/rubric/referenceAnswer, 构造
//   evaluate context bundle, 单次 LLM 调用产出 verdict, zod 校验后返回。
//
//   response 直接是 EvaluateResult (itemKind + verdict + telemetry)——前端取
//   verdict 存进 essayDrafts[id].aiScore / freeResponse.aiScore 持久化槽位。
// ─────────────────────────────────────────────────────────────────────────────
export const EvaluateRequestSchema = z.object({
    /** 被评题型 (判别 handler 内的 schema 解析 + prompt 模板路由)。 */
    itemKind: EvaluateItemKindSchema,
    /** 被评 item 的完整 JSON (Essay 或 FreeResponse)。handler 用对应 schema 解析。 */
    itemJson: z.unknown(),
    /** 用户提交的答案 (human_input provenance · §2.1)。 */
    userAnswer: z.string().min(1).max(20_000),
    /** 可选: 历史 attempt (跨设备诊断需云化 · §13.4 目标 · 当前本地传入)。 */
    historyAttempts: z.array(z.object({
        score: z.number().min(0).max(100),
        scoredAt: z.string().datetime(),
        feedback: z.string(),
    })).max(10).optional(),
});
/** response 直接复用 EvaluateResultSchema (itemKind + verdict + telemetry)。 */
export { EvaluateResultSchema as EvaluateResponseSchema };
// ─────────────────────────────────────────────────────────────────────────────
//   POST /ingest/diagnose · 19BO+ Line 2 B4 · AI 弱项诊断
//
//   同步调用 (非 async job · 与 /ingest/evaluate 同构 · 单次 LLM 调用返回 verdict)。
//   前端传客户端聚合的错题统计 (byCase/byCategory/byCognitive/recentTrend · 形状对齐
//   web/src/lib/stats.ts WrongAnswerStats), 后端构造 diagnose context bundle, 单次
//   LLM 调用产出弱点诊断 verdict (只读 · §12.2 铁律 4 不包 EditableItem), zod 校验后返回。
//
//   B4-a 决策 (roadmap 批次 D): 诊断 context 走 client 传参 (像 evaluate 传 userAnswer),
//   不做云化。wrongAnswers 的云化是 B4-b (批次 E · 前置 DEBT-19BO+-LINTREPORT-IDB-PERSIST
//   已关账 · §13.4 三开放问题待决)。故本 endpoint 接受序列化的聚合统计而非 course/run 引用。
//
//   response 直接是 DiagnoseResult (verdict + telemetry)——前端取 verdict 渲染只读卡片。
// ─────────────────────────────────────────────────────────────────────────────
/** 错题按章节聚合 (对齐 WrongAnswerStats.byCase · 前端序列化传入)。 */
const WrongByCaseSchema = z.object({
    caseId: z.string().min(1),
    caseName: z.string().min(1),
    count: z.number().int().nonnegative(),
    unresolved: z.number().int().nonnegative(),
}).strict();
/** 错题按题型聚合 (对齐 WrongAnswerStats.byCategory)。 */
const WrongByCategorySchema = z.object({
    category: z.string().min(1),
    count: z.number().int().nonnegative(),
    unresolved: z.number().int().nonnegative(),
}).strict();
/** 错题按认知层级 (Bloom) 聚合 (对齐 WrongAnswerStats.byCognitive)。 */
const WrongByCognitiveSchema = z.object({
    level: z.string().min(1),
    count: z.number().int().nonnegative(),
}).strict();
/** 错题时间序列 (对齐 WrongAnswerStats.recentTrend · dayOffset 0=今天)。 */
const WrongTrendSchema = z.object({
    dayOffset: z.number().int().min(0),
    wrongEvents: z.number().int().nonnegative(),
}).strict();
/**
 * Diagnose 请求体。
 *
 * B4-a client 传参模式：前端用 web/src/lib/stats.ts 的 wrongAnswerStats() 聚合本地
 * progress.wrongAnswers，把结果序列化进请求体。后端不读 IDB/不查 run——纯函数式消费
 * 这些聚合数据构造 context。这让 diagnose 在冷启动 (无云化基础) 时即可用。
 *
 * 各字段对齐 DiagnoseNeeds 的三个 need：
 *   - wrongAnswerAggregate ← byCase + byCategory + total/unresolved/resolved
 *   - itemMetadata         ← byCognitive (Bloom 归因)
 *   - timeSeries           ← recentTrend
 */
export const DiagnoseRequestSchema = z.object({
    /** 错题按章节分布 (DiagnoseNeeds.wrongAnswerAggregate 的 chapter 维度)。 */
    byCase: z.array(WrongByCaseSchema).max(50),
    /** 错题按题型分布 (DiagnoseNeeds.wrongAnswerAggregate 的 category 维度)。 */
    byCategory: z.array(WrongByCategorySchema).max(20),
    /** 错题按 Bloom 认知层级分布 (DiagnoseNeeds.itemMetadata · 归因用)。 */
    byCognitive: z.array(WrongByCognitiveSchema).max(10),
    /** 14 天错题趋势 (DiagnoseNeeds.timeSeries · 趋势判断用)。 */
    recentTrend: z.array(WrongTrendSchema).max(14),
    /** 错题总数。 */
    total: z.number().int().nonnegative(),
    /** 未解决错题数。 */
    unresolved: z.number().int().nonnegative(),
    /** 已解决错题数。 */
    resolved: z.number().int().nonnegative(),
}).strict();
/** response 直接复用 DiagnoseResultSchema (verdict + telemetry)。 */
export { DiagnoseResultSchema as DiagnoseResponseSchema };
//# sourceMappingURL=v4.js.map